// Defensive coordinate guards. The Mapbox JS SDK accepts non-finite values in
// `setLngLat` (it stores them) but then throws a hard
// `Cannot read properties of undefined (reading 'lng')` from inside
// `Marker._update` → `new LngLat()` the next time the SDK reprojects. That
// throw bubbles out of React render and lands in the page-level
// ErrorBoundary, blanking the whole route detail screen.
//
// Wrap every value that flows into `setLngLat`, `easeTo`, `flyTo`,
// `addSource` etc. through these helpers so a single malformed point can't
// take down the whole map.
//
// See `wiki/pitfalls/routemap-undefined-waypoint-lnglat.md`.

export function isFiniteLngLat(lon: unknown, lat: unknown): boolean {
  return (
    typeof lon === 'number' &&
    Number.isFinite(lon) &&
    typeof lat === 'number' &&
    Number.isFinite(lat)
  )
}

export interface PointLike {
  lon?: unknown
  lat?: unknown
}

export function isFinitePoint<T extends PointLike>(
  point: T | null | undefined,
): point is T & { lon: number; lat: number } {
  if (point == null) return false
  return isFiniteLngLat(point.lon, point.lat)
}

/** Filter a list of waypoint-like records down to those with finite
 *  `lon`/`lat`. `onInvalid` runs once per dropped entry so callers can log
 *  in dev (we deliberately do NOT log from here so server-side or test
 *  consumers stay quiet). */
export function filterFiniteWaypoints<T extends PointLike>(
  list: readonly T[] | null | undefined,
  onInvalid?: (entry: T, index: number) => void,
): T[] {
  if (!list || list.length === 0) return []
  const out: T[] = []
  for (let i = 0; i < list.length; i++) {
    const entry = list[i] as T
    if (isFinitePoint(entry)) {
      out.push(entry)
    } else if (onInvalid) {
      onInvalid(entry, i)
    }
  }
  return out
}
